/*
 * The investor rail's organisation menu, **as the unified UI draws it**. Everything from `.menu`
 * down applies only to a user `useUnifiedShell` says is in the new shell; a user who is not gets
 * the `styled-components` menu at the foot of `organization-selector-sidebar.tsx`, untouched. Only
 * `.container` is shared, being the wrapper both menus position against. That split is deliberate
 * and the reasoning is on the component — the short version is that the unified shell is opt-in
 * and early, so a fix made for it must not reach an investor who has not been opted in.
 *
 * The trigger above the menu is the shared `ContextSelector` (PLT-6220) and every number in it is
 * the `design/40-shell-and-pages.md` "Sidebar" spec's. This file is only the menu, which stays
 * this rail's own: `ContextSelector` deliberately does not own a menu, so it cannot be the
 * `react-aria` element a `MenuTrigger` wires itself to.
 *
 * The chrome and the rows are the design system's dropdown popover (`dropdowns/Dropdowns.module.css`)
 * rather than a fourth set of numbers: `gray-300` hairline, `sm` radius, `xs` padding, `xs`
 * between rows, a row of `md` regular `gray-800` text at `xs sm` padding, `gray-100` hover and
 * focus, `gray-200` selected and `gray-300` selected-and-hovered. That file is the nearest
 * sibling, being a popover holding a list of selectable rows, and matching the nearest sibling
 * and saying so is what `design/50-components.md` asks for where a case is not itself specified.
 *
 * What that changed, from Janne's review of the shell on staging (PLT-6648) and the launch audit
 * (PLT-6945):
 *
 * - the menu no longer sizes to its content (`max-content` up to 500px against a 296px rail, so
 *   it overhung the content column) — it matches the trigger and truncates instead;
 * - the rows no longer sit `md` (12px) apart, which read as a stack of cards rather than a list;
 * - no monogram on the rows and no uppercase group label above the field: the trigger is the
 *   text variant, and a `teal-500` tile per row spent the positive-impact hue on a mark that
 *   encodes nothing;
 * - the names are regular, as a menu row is, not semibold: seven semibold names down a list are
 *   seven headings;
 * - the search field is the design system's `Input`, so its ring is the platform one on a
 *   full-height field instead of a heavy double outline around a 24px-tall box;
 * - the list's height is a whole number of rows, so the scroll boundary lands between two rows;
 * - the row focus ring is `--focus-ring__*` rather than the retiring `--focus-outlineColor`.
 *
 * No `box-shadow`, which the old menu had: the design system's dropdown popover carries none, and
 * surfaces here are separated by a border rather than by shading
 * (`design/10-foundations.md` "Colour").
 */

.container {
  position: relative;
  inline-size: 100%;
}

.menu {
  /*
   * The row's height, derived rather than frozen (`design/10-foundations.md`: express the
   * derivation): one line of `md` text at its paired `sm` (20px) leading, plus the row's `xs`
   * block padding. `.list`'s `max-block-size` is a whole number of these plus the gaps between
   * them, so the scroll boundary always lands between two rows.
   */
  --org-menu-row-block-size: calc(var(--line-height__sm) + 2 * var(--space__xs));
  --org-menu-visible-rows: 7;

  position: absolute;
  inset-block-start: calc(100% + var(--space__xs));
  inset-inline-start: 0;
  /*
   * The trigger's width, and no wider. `.container` is exactly as wide as the `ContextSelector`
   * inside it, so `100%` here is that box — the same thing the design system's popover does with
   * `min-inline-size: var(--trigger-width)`. A long organisation name truncates in the row
   * instead of widening the menu past the rail, which is PLT-6224's rule for the nav rows too.
   */
  inline-size: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  padding: var(--space__xs);
  background-color: var(--color__white);
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__sm);
  /* Above the rail and the content column, as the corporate rail's menu is. */
  z-index: 1001;
}

.list {
  display: flex;
  flex-direction: column;
  row-gap: var(--space__xs);
  max-block-size: calc(
    var(--org-menu-visible-rows) * var(--org-menu-row-block-size) + (var(--org-menu-visible-rows) - 1) *
      var(--space__xs)
  );
  overflow-y: auto;
}

.row {
  display: flex;
  align-items: center;
  inline-size: 100%;
  box-sizing: border-box;
  padding: var(--space__xs) var(--space__sm);
  border: none;
  background: transparent;
  /* The design system's menu-item radius, written as its own expression so the two stay equal. */
  border-radius: calc(var(--border-radius__lg) - var(--space__xs));
  text-align: left;
  cursor: pointer;
}

/*
 * Gray, as the design system's menu rows hover: blue in this product is the selected segment of a
 * pill tab switcher and nothing else (`design/10-foundations.md` "Every colour has one job").
 */
.row:hover,
.row:focus-visible {
  background: var(--color__gray-100);
}

/*
 * Inset, where the platform ring normally sits 2px outside its control.
 *
 * `.list` above scrolls, and CSS cannot scroll one axis while overflowing the other: setting
 * `overflow-y` makes `overflow-x` compute as `auto` too. An outset ring therefore loses its left
 * and right sides to the scroll box, and a focused row wears two horizontal bars instead of a
 * ring (PLT-6696). A full-bleed row inside a scroller takes a negative offset instead
 * (design/50-components.md, "Focus and interaction states"). The colour and width stay the
 * platform ring's, so only where it sits changes.
 */
.row:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: calc(-1 * var(--focus-ring__width));
}

/*
 * `gray-200`, the design system's selected menu row, darkening a step on hover so the selected
 * row stays the heaviest in the list whether the pointer is on it or not.
 */
.row[data-selected] {
  background: var(--color__gray-200);
}

.row[data-selected]:hover,
.row[data-selected]:focus-visible {
  background: var(--color__gray-300);
}

.name {
  flex: 1;
  /* Without this the flex item's minimum is its intrinsic width, so a long organisation name
     pushes the menu wider than the rail instead of ellipsising — the same reason
     `ContextSelector` sets it on the trigger's name. */
  min-inline-size: 0;
  font-size: var(--font-size__md);
  line-height: var(--line-height__sm);
  font-weight: var(--font-weight__regular);
  color: var(--color__gray-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* `gray-600`, the meta floor on white (`DESIGN.md` rule 22). */
.empty {
  padding: var(--space__sm);
  font-size: var(--font-size__md);
  color: var(--color__gray-600);
  text-align: center;
}
